import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { BrandLogo } from "@/components/BrandLogo";
import {
  LayoutDashboard,
  Car,
  Package,
  Users,
  Bell,
  CreditCard,
  FileText,
  Settings,
  Sliders,
  ChevronRight,
  ChevronDown,
  Bike,
  MessageSquare,
  Ticket,
  ShieldCheck,
  FileQuestion,
  Banknote,
  BookOpen,
} from "lucide-react";

type NavItem = {
  to?: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { to: string; label: string; icon: React.ComponentType<{ className?: string }> }[];
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const navSections: NavSection[] = [
  {
    title: "General",
    items: [
      { to: "/", label: "Panel", icon: LayoutDashboard },
    ],
  },
  {
    title: "Catálogo",
    items: [
      {
        label: "Vehículos",
        icon: Car,
        children: [
          { to: "/vehicles/motorcycles", label: "Motocicletas", icon: Bike },
          { to: "/vehicles/automobiles", label: "Automóviles", icon: Car },
        ],
      },
      { to: "/parts", label: "Repuestos", icon: Package },
    ],
  },
  {
    title: "Ventas",
    items: [
      { to: "/quotes", label: "Cotizaciones", icon: FileText },
      { to: "/quote-requests", label: "Solicitudes Web", icon: FileQuestion },
      { to: "/financings", label: "Financiamiento", icon: CreditCard },
      { to: "/payments", label: "Pagos", icon: Banknote },
      { to: "/garantias", label: "Garantías", icon: ShieldCheck },
    ],
  },
  {
    title: "Comunidad",
    items: [
      { to: "/users", label: "Usuarios", icon: Users },
      { to: "/raffles", label: "Rifas", icon: Ticket },
      { to: "/notifications", label: "Notificaciones", icon: Bell },
      { to: "/suggestions", label: "Sugerencias", icon: MessageSquare },
    ],
  },
  {
    title: "Configuración",
    items: [
      { to: "/configuracion", label: "Ajustes", icon: Sliders },
      { to: "/documentacion", label: "Documentación", icon: BookOpen },
      { to: "/more", label: "Más", icon: Settings },
    ],
  },
];

type SidebarProps = {
  onNavigate?: () => void;
};

export function Sidebar({ onNavigate }: SidebarProps) {
  const location = useLocation();
  const isVehiclesSection = location.pathname.startsWith("/vehicles");
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({});

  const renderItem = (item: NavItem) => {
    if (item.children) {
      const defaultOpen = item.label === "Vehículos" && isVehiclesSection;
      const isOpen = sectionOpen[item.label] ?? defaultOpen;
      return (
        <div key={item.label}>
          <button
            type="button"
            onClick={() =>
              setSectionOpen((prev) => ({
                ...prev,
                [item.label]: !(prev[item.label] ?? defaultOpen),
              }))
            }
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
              isOpen ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {item.label}
            <ChevronDown
              className={cn("ml-auto h-4 w-4 shrink-0 transition-transform", isOpen && "rotate-180")}
            />
          </button>
          {isOpen && (
            <div className="ml-4 mt-0.5 space-y-0.5 border-l border-zinc-700/80 pl-2">
              {item.children.map((child) => {
                const isActive = location.pathname === child.to;
                return (
                  <Link
                    key={child.to}
                    to={child.to}
                    onClick={() => onNavigate?.()}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                      isActive
                        ? "bg-primary/20 font-medium text-primary"
                        : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                    )}
                  >
                    <child.icon className="h-3.5 w-3.5 shrink-0" />
                    {child.label}
                    {isActive && <ChevronRight className="ml-auto h-3.5 w-3.5" />}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const isActive =
      location.pathname === item.to ||
      (item.to !== "/" && item.to && location.pathname.startsWith(item.to));
    return (
      <Link
        key={item.to ?? item.label}
        to={item.to!}
        onClick={() => onNavigate?.()}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isActive
            ? "bg-primary/20 text-primary"
            : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {item.label}
        {isActive && <ChevronRight className="ml-auto h-4 w-4" />}
      </Link>
    );
  };

  return (
    <aside className="flex h-full w-56 flex-col border-r border-zinc-700/80 bg-zinc-900 font-display text-zinc-100">
      <div className="flex h-14 items-center border-b border-zinc-700/80 px-4 [&_.text-foreground]:text-zinc-100 [&_.text-muted-foreground]:text-zinc-400">
        <BrandLogo showLabel compact={false} />
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        {navSections.map((section, idx) => (
          <div key={section.title} className={idx > 0 ? "mt-4" : ""}>
            <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
              {section.title}
            </p>
            <div className="space-y-0.5">
              {section.items.map(renderItem)}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
